import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiGift,
  FiHome,
  FiPhone,
  FiShield,
  FiUser
} from 'react-icons/fi';

function GuestRegistration() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    room_no: '',
    checkin: '',
    checkout: '',
    dob: '',
    anniversary: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [isDobRequired, setIsDobRequired] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const apiBase = useMemo(() => {
    return process.env.REACT_APP_API_URL || window.location.origin;
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${apiBase}/api/public/guest-registration-settings`);
        if (!response.ok) return;
        const data = await response.json();
        setIsDobRequired(data?.dob_required_for_guest_creation !== false);
      } catch (error) {
        setIsDobRequired(true);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [apiBase]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const response = await fetch(`${apiBase}/api/public/guest-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to submit registration');
      }

      setResult({ type: 'success', message: data?.message || 'Registration submitted successfully.' });
      setFormData({
        name: '',
        phone: '',
        room_no: '',
        checkin: '',
        checkout: '',
        dob: '',
        anniversary: ''
      });
    } catch (error) {
      setResult({ type: 'error', message: error.message || 'Failed to submit registration' });
    } finally {
      setSubmitting(false);
    }
  };

  const maxToday = new Date().toISOString().split('T')[0];

  const baseInputClass =
    'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 transition';

  return (
    <div className="min-h-screen bg-[#f4f5fb] px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <div className="grid gap-6 lg:grid-cols-5">
          <aside className="fleet-card lg:col-span-2 p-6">
            <div className="fleet-pill bg-gray-900 text-white border-gray-900">
              Guest Self Check-in
            </div>

            <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900">Welcome to Oleander</h1>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Fill your details once and our team will take care of the rest. A welcome WhatsApp message is shared after successful submission.
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <FiShield className="mt-0.5 h-4 w-4 text-gray-700" />
                <p className="text-xs text-gray-700">Your details are used only for stay assistance and personalized service.</p>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <FiCalendar className="mt-0.5 h-4 w-4 text-gray-700" />
                <p className="text-xs text-gray-700">Sharing check-in/check-out helps us activate support at the right time.</p>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <FiGift className="mt-0.5 h-4 w-4 text-gray-700" />
                <p className="text-xs text-gray-700">Optional celebration dates help the team personalize your experience.</p>
              </div>
            </div>
          </aside>

          <main className="fleet-card lg:col-span-3 p-6 sm:p-8">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Check-in Details</h2>
                <p className="mt-1 text-sm text-gray-600">Please complete the form below.</p>
              </div>
              {loadingSettings ? (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">Loading rules...</span>
              ) : (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  DOB {isDobRequired ? 'Required' : 'Optional'}
                </span>
              )}
            </div>

            {result && (
              <div
                className={`mt-5 flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm ${
                  result.type === 'success'
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                {result.type === 'success' ? (
                  <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                ) : (
                  <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                )}
                <span>{result.message}</span>
              </div>
            )}

            <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
              <section className="rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-600">Personal Details</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <FiUser className="h-4 w-4" />
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={baseInputClass}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <FiPhone className="h-4 w-4" />
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={baseInputClass}
                      placeholder="10-digit mobile number"
                      inputMode="numeric"
                      maxLength={10}
                    />
                    <p className="mt-1 text-xs text-gray-500">Country code is auto-detected by system rules.</p>
                  </div>

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <FiHome className="h-4 w-4" />
                      Room Number
                    </label>
                    <input
                      type="text"
                      value={formData.room_no}
                      onChange={(e) => handleChange('room_no', e.target.value)}
                      className={baseInputClass}
                      placeholder="Example: 309"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Date of Birth {isDobRequired ? <span className="text-red-500">*</span> : <span className="text-gray-500">(Optional)</span>}
                    </label>
                    <input
                      type="date"
                      required={isDobRequired}
                      value={formData.dob}
                      onChange={(e) => handleChange('dob', e.target.value)}
                      className={baseInputClass}
                      max={maxToday}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-600">Stay Details</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Check-in Date</label>
                    <input
                      type="date"
                      value={formData.checkin}
                      onChange={(e) => handleChange('checkin', e.target.value)}
                      className={baseInputClass}
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Check-out Date</label>
                    <input
                      type="date"
                      value={formData.checkout}
                      onChange={(e) => handleChange('checkout', e.target.value)}
                      className={baseInputClass}
                      min={formData.checkin || ''}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-600">Occasion (Optional)</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Anniversary Date</label>
                    <input
                      type="date"
                      value={formData.anniversary}
                      onChange={(e) => handleChange('anniversary', e.target.value)}
                      className={baseInputClass}
                    />
                  </div>
                </div>
              </section>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Submitting your check-in...' : 'Submit Check-in'}
              </button>

              <p className="text-center text-xs text-gray-500">Need help? Please contact the front office team.</p>
            </form>
          </main>
        </div>
      </div>
    </div>
  );
}

export default GuestRegistration;
